import React, { useEffect, useMemo, useState } from 'react';
import "../Styles/CreateUser.css";
import * as yup from "yup";
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { ErrorMessage, Formik, Form, Field } from "formik";
import Axios from "axios";
import { toast, ToastContainer, toast as toastHelper } from 'react-toastify';
import MDUserProfileHeader from './MDUserProfileHeader';

const MDAssignKey = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { mdId } = useParams();

  const [siList, setSiList] = useState([]);
  const [isLoadingSi, setIsLoadingSi] = useState(false);

  const parseSerials = (raw) => {
    if (!raw) return [];
    return Array.from(
      new Set(
        String(raw)
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      )
    );
  };

  useEffect(() => {
    const fetchSiList = async () => {
      try {
        setIsLoadingSi(true);
        const resp = await Axios.post('/get_si_list_by_md', { mdId });
        if (resp?.data?.success) {
          setSiList(resp.data.data || []);
        } else {
          setSiList([]);
          toast.error(resp?.data?.msg || "Failed to load System Integrators.", {
            position: "top-right",
            autoClose: 4000,
            theme: "light",
          });
        }
      } catch (e) {
        setSiList([]);
        toast.error("Failed to load System Integrators.", {
          position: "top-right",
          autoClose: 4000,
          theme: "light",
        });
      } finally {
        setIsLoadingSi(false);
      }
    };

    if (mdId) fetchSiList();
  }, [mdId]);

  const validationSchema = useMemo(() => {
    return yup.object().shape({
      siId: yup.string().trim().required("Please select a company."),
      yubikeyserialnumber: yup
        .string()
        .required("Please input YubiKey serial number(s).")
        .test(
          "serials-not-empty",
          "Please input YubiKey serial number(s).",
          (val) => parseSerials(val).length > 0
        ),
    });
  }, []);

  const handleAssignKeysToSI = async (values, { resetForm, setSubmitting }) => {
    try {
      const operatorEmail = localStorage.getItem('userEmail') || "";
      const serials = parseSerials(values.yubikeyserialnumber);

      const resp = await Axios.post('/assign_velox_cloud_keys_to_si', {
        mdId: mdId,
        siId: values.siId,
        serial_numbers: serials,
        operator_email: operatorEmail,
      });

      if (resp?.data?.success) {
        toast.success(resp?.data?.msg || "Assigned successfully!", {
          position: "top-right",
          autoClose: 5000,
          theme: "light",
        });

        // Optional: show skipped details if any
        if (Array.isArray(resp?.data?.skipped) && resp.data.skipped.length > 0) {
          const firstFew = resp.data.skipped.slice(0, 5);
          const details = firstFew
            .map((x) => `${x.serial}: ${x.reason}`)
            .join(" | ");

          toast.info(`Some keys were skipped. ${details}${resp.data.skipped.length > 5 ? " ..." : ""}`, {
            position: "top-right",
            autoClose: 8000,
            theme: "light",
          });
        }

        resetForm();
      } else {
        toast.error(resp?.data?.msg || "Assign failed, please try again!", {
          position: "top-right",
          autoClose: 5000,
          theme: "light",
        });
      }
    } catch (e) {
      toast.error("Assign failed, please try again!", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="dashboard">
      <MDUserProfileHeader isAdmin={true} />

      <div className="all-users-card">
        <h1 className="Title-alluser">Assign VELOX CLOUD Key to System Integrator</h1>

        <Formik
          initialValues={{
            siId: "",
            yubikeyserialnumber: "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleAssignKeysToSI}
        >
          {({ isSubmitting }) => (
            <Form className="create-user-form">
              <div className="create-user-container">

                <div className="create-user-field">
                  <label className="create-user-label">Company</label>
                  <Field as="select" name="siId" className="create-user-input">
                    <option value="">{isLoadingSi ? "Loading..." : "Select a company"}</option>
                    {siList.map((si) => (
                      <option key={si.si_id} value={si.si_id}>
                        {si.company_name || si.si_id}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage name="siId" component="div" className="error-message" />
                </div>

                <div className="create-user-field">
                  <label className="create-user-label">YubiKey Serial Number(s)</label>
                  <Field
                    name="yubikeyserialnumber"
                    className="create-user-input"
                    placeholder="e.g. 12345678, 87654321"
                  />
                  <div className="create-user-hint">
                    You can input multiple serial numbers separated by commas (,).
                  </div>
                  <ErrorMessage name="yubikeyserialnumber" component="div" className="error-message" />
                </div>

                <button type="submit" className="create-user-button" disabled={isSubmitting || isLoadingSi}>
                  {isSubmitting ? "Assigning..." : "Assign"}
                </button>
              </div>
            </Form>
          )}
        </Formik>

        <ToastContainer />
      </div>
    </div>
  );
};

export default MDAssignKey;
